// How the Analytics area writes numbers, money, durations and dates. One place, so a chart label, a tile and the table of the same number always
// agree. Money is INR in minor units (paise): "₹4,82,000" with the lakh grouping of India; paise show only when there are some.

const RUPEES = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});
const RUPEES_WITH_PAISE = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const COUNT = new Intl.NumberFormat("en-IN");

/** `48200000` (paise) becomes `₹4,82,000`. */
export function moneyText(minor: number): string {
  return minor % 100 === 0 ? RUPEES.format(minor / 100) : RUPEES_WITH_PAISE.format(minor / 100);
}

/** `1284` becomes `1,284`; the Indian grouping applies from 1,00,000. */
export function countText(value: number): string {
  return COUNT.format(value);
}

/** A whole percent: `0.684` becomes `68%`. */
export function percentText(value: number): string {
  return `${Math.round(value * 100)}%`;
}

/** `13200` becomes `3 h 40 min`, `540` `9 min`, `9` `9 s`. Shows at most two units. */
export function durationText(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours < 48) return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours === 0 ? `${days} d` : `${days} d ${restHours} h`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** `2026-09` becomes `Sep 2026`; `short` leaves the year out (`Sep`). */
export function monthText(month: string, short = false): string {
  const [year, number] = month.split("-");
  const name = MONTHS[Number(number) - 1] ?? month;
  return short ? name : `${name} ${year}`;
}

/** A day or an hour in the zone of the page: `29 Oct`, or `29 Oct, 14:00` for an hourly bucket. */
export function bucketText(iso: string, tz: string, interval: "hour" | "day" | "week"): string {
  const date = new Date(iso);
  const day = new Intl.DateTimeFormat("en-GB", { timeZone: tz, day: "numeric", month: "short" });
  if (interval !== "hour") return day.format(date);
  const time = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${day.format(date)}, ${time.format(date)}`;
}

/** A date for the privacy line and the header: `14 Mar 2025`. */
export function dateText(iso: string, tz: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(iso));
}
